import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface UserAlert {
  id: string;
  user_id: string;
  product_id: string;
  alert_type: 'restock' | 'discount';
  message: string;
  product_title: string;
  product_image_url: string | null;
  discount_percentage: number | null;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
}

export const WishlistAlerts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const loadUnreadAlerts = async () => {
      const { data, error } = await supabase
        .from('user_alerts')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error loading alerts:', error);
        return;
      }

      if (data && data.length > 0) {
        data.forEach(alert => {
          if (alert.alert_type === 'restock' || alert.alert_type === 'discount') {
            showAlert(alert as UserAlert);
          }
        });
      }
    };

    loadUnreadAlerts();

    // Refetch on tab focus (replaces realtime sub)
    const onFocus = () => { loadUnreadAlerts(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user]);

  const showAlert = (alert: UserAlert) => {
    const emoji = alert.alert_type === 'restock' ? '🎉' : '🏷️';
    
    toast(alert.message, {
      description: `Click to view ${alert.product_title}`,
      duration: 6000,
      action: {
        label: 'View Product',
        onClick: () => handleAlertClick(alert),
      },
    });
  };

  const handleAlertClick = async (alert: UserAlert) => {
    // Mark as read
    await supabase
      .from('user_alerts')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', alert.id);

    // Navigate to product
    navigate(`/product/${alert.product_id}`);
  };

  return null;
};
